import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import * as api from './api.ts';
import { Button, Finding, Status, type Tone } from './ui.tsx';
import { clock } from './warden-areas.tsx';

/**
 * The Direct record on the live view (migration 0035, `api/src/routes/direct.ts`):
 * the ground as it stands in THIS event, the append-only log of every change
 * to it, and what people on the ground are reporting in.
 *
 * The rule the surface enforces: **a change to the ground is a message, or it
 * is worse than nothing.** Four hundred people are already walking towards the
 * point being closed, so a closure names where they go instead, the operator
 * writes the words and sees them before sending, and the last open point
 * cannot close at all (the server refuses both; this surface says why in
 * words). Inbound reports are *readings*, never messages: there is no
 * forward-this-report control, because a crowd-sourced instruction is how two
 * hundred people end up running towards the thing.
 */

const REFRESH_MS = 5_000;

/** The three queries, shared by the panes and by the live view's Do next list. */
export function useDirect(orgId: string, eventId: string, enabled = true) {
  const ground = useQuery({
    queryKey: ['ground', orgId, eventId],
    queryFn: () => api.getGround(orgId, eventId),
    refetchInterval: REFRESH_MS,
    enabled,
  });
  const updates = useQuery({
    queryKey: ['updates', orgId, eventId],
    queryFn: () => api.getUpdates(orgId, eventId),
    refetchInterval: REFRESH_MS,
    enabled,
  });
  const reports = useQuery({
    queryKey: ['reports', orgId, eventId],
    queryFn: () => api.getReports(orgId, eventId),
    refetchInterval: REFRESH_MS,
    enabled,
  });
  return { ground, updates, reports };
}

export const REPORT_WORDS: Record<api.ReportKind, string> = {
  hazard: 'Hazard',
  needs_help: 'Somebody needs help',
  blocked_route: 'Route blocked',
  other: 'Report',
};

/** Where a report is about, in the reporter's own terms. */
export function reportPlace(r: api.InboundReport): string | null {
  return r.zone_name ?? r.place_said ?? null;
}

/**
 * The Do next line a report earns. Only a NEW report that somebody else has
 * independently corroborated, or a report that somebody needs help, makes the
 * list — one person's reading is on the Reports pane, not in the operator's
 * face.
 */
export function reportFinding(r: api.InboundReport): string | null {
  if (r.state !== 'new') return null;
  const where = reportPlace(r);
  const at = where ? ` in ${where}` : '';
  if (r.kind === 'needs_help') {
    return r.corroborations > 0
      ? `${r.corroborations + 1} people report somebody needs help${at}.`
      : `Somebody reports a person needs help${at}.`;
  }
  if (r.corroborations === 0) return null;
  const what =
    r.kind === 'hazard'
      ? 'a hazard'
      : r.kind === 'blocked_route'
        ? 'a blocked route'
        : 'the same thing';
  return `${r.corroborations + 1} people independently report ${what}${at}.`;
}

const UPDATE_WORDS: Record<api.UpdateKind, string> = {
  instruction: 'Instruction',
  point_closed: 'Point closed',
  point_opened: 'Point reopened',
  hazard_marked: 'Hazard marked',
  hazard_cleared: 'Hazard cleared',
  note: 'Note',
};

/**
 * The ground: every assembly point of the site with its state in this event,
 * every area with its hazard axis, the composer, and the log. Controls appear
 * only while the event is active — after the all-clear the record is what it
 * was.
 */
export function GroundPane({
  orgId,
  eventId,
  active,
  kind,
  direct,
}: {
  orgId: string;
  eventId: string;
  active: boolean;
  /**
   * The kind of point this procedure sends people to (the map's rule):
   * evacuation → outdoor musters, lockdown → shelters inside, null → none.
   * Closing an evacuation point during a lockdown is not a decision anybody
   * is making, so other kinds stay off the list — and off the redirect list.
   */
  kind: api.AssemblyKind | null;
  direct: ReturnType<typeof useDirect>;
}) {
  const qc = useQueryClient();
  const { ground, updates } = direct;
  const [closing, setClosing] = useState<string | null>(null);
  const [marking, setMarking] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);

  const post = useMutation({
    mutationFn: (body: Parameters<typeof api.postUpdate>[2]) =>
      api.postUpdate(orgId, eventId, body),
    onSuccess: () => {
      setClosing(null);
      setMarking(null);
      setComposing(false);
      void qc.invalidateQueries({ queryKey: ['ground', orgId, eventId] });
      void qc.invalidateQueries({ queryKey: ['updates', orgId, eventId] });
    },
  });

  const points = groundPointsOf(ground.data?.points, kind);
  const zones = ground.data?.zones ?? [];
  const open = points.filter((p) => p.open);
  const err = post.error ? api.errorMessage(post.error) : null;

  return (
    <div className="space-y-4">
      {ground.isError && !ground.data ? (
        <Finding>
          The ground did not load.{' '}
          <Button variant="quiet" label="Retry the ground" onClick={() => void ground.refetch()}>
            Retry
          </Button>
        </Finding>
      ) : null}

      {points.length > 0 ? (
        <ul
          className="m-0 list-none divide-y divide-[var(--line)] p-0"
          aria-label="Assembly points"
        >
          {points.map((p) => {
            const lastOpen = p.open && open.length <= 1;
            return (
              <li key={p.id} className="py-2 text-[14px]">
                <div className="flex items-center gap-3">
                  <span className="min-w-0 flex-1 truncate">{p.name}</span>
                  <Status tone={p.open ? 'safe' : 'alert'}>{p.open ? 'Open' : 'Closed'}</Status>
                  {active && closing !== p.id ? (
                    p.open ? (
                      <Button
                        disabled={lastOpen || post.isPending}
                        label={
                          lastOpen
                            ? `${p.name} is the only open point and cannot be closed`
                            : `Close ${p.name}`
                        }
                        onClick={() => {
                          setMarking(null);
                          setComposing(false);
                          setClosing(p.id);
                        }}
                      >
                        Close
                      </Button>
                    ) : (
                      <Button
                        disabled={post.isPending}
                        label={`Reopen ${p.name}`}
                        onClick={() =>
                          post.mutate({
                            kind: 'point_opened',
                            subject_id: p.id,
                            body: `${p.name} is open again.`,
                          })
                        }
                      >
                        Reopen
                      </Button>
                    )
                  ) : null}
                </div>
                {!p.open && p.closed_reason ? (
                  <p className="mt-0.5 text-[13px] text-[var(--ink-2)]">“{p.closed_reason}”</p>
                ) : null}
                {lastOpen && active && points.length > 1 ? (
                  <p className="mt-0.5 text-[12px] text-[var(--ink-2)]">
                    Only open point — reopen another before closing it.
                  </p>
                ) : null}
                {closing === p.id ? (
                  <ClosePointForm
                    point={p}
                    openPoints={open.filter((o) => o.id !== p.id)}
                    pending={post.isPending}
                    onSend={(redirectToId, body) =>
                      post.mutate({
                        kind: 'point_closed',
                        subject_id: p.id,
                        redirect_to_id: redirectToId,
                        body,
                      })
                    }
                    onCancel={() => {
                      post.reset();
                      setClosing(null);
                    }}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : ground.data ? (
        <p className="text-[13px] text-[var(--ink-2)]">
          {kind === null
            ? 'This procedure sends people to no point.'
            : 'This building has no point of the kind this procedure sends people to.'}
        </p>
      ) : null}

      {zones.length > 0 ? (
        <ul
          className="m-0 list-none divide-y divide-[var(--line)] p-0"
          aria-label="Areas and hazards"
        >
          {zones.map((z) => (
            <li key={z.id} className="py-2 text-[14px]">
              <div className="flex items-center gap-3">
                <span className="min-w-0 flex-1 truncate">{z.name}</span>
                {z.hazard ? <Status tone="alert">Hazard</Status> : null}
                {active && marking !== z.id ? (
                  z.hazard ? (
                    <Button
                      disabled={post.isPending}
                      label={`Clear the hazard in ${z.name}`}
                      onClick={() =>
                        post.mutate({
                          kind: 'hazard_cleared',
                          subject_id: z.id,
                          body: `${z.name} is clear of the hazard.`,
                        })
                      }
                    >
                      Clear hazard
                    </Button>
                  ) : (
                    <Button
                      disabled={post.isPending}
                      label={`Mark a hazard in ${z.name}`}
                      onClick={() => {
                        setClosing(null);
                        setComposing(false);
                        setMarking(z.id);
                      }}
                    >
                      Mark hazard
                    </Button>
                  )
                ) : null}
              </div>
              {z.hazard && z.hazard_note ? (
                <p className="mt-0.5 text-[13px] text-[var(--ink-2)]">“{z.hazard_note}”</p>
              ) : null}
              {marking === z.id ? (
                <WordsForm
                  label={`What is the hazard in ${z.name}?`}
                  initial={`Avoid ${z.name}. `}
                  send="Mark hazard and tell everyone"
                  pending={post.isPending}
                  onSend={(body) => post.mutate({ kind: 'hazard_marked', subject_id: z.id, body })}
                  onCancel={() => {
                    post.reset();
                    setMarking(null);
                  }}
                />
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {active ? (
        composing ? (
          <WordsForm
            label="Tell everyone who was alerted"
            initial=""
            send="Send to everyone"
            pending={post.isPending}
            onSend={(body) => post.mutate({ kind: 'instruction', body })}
            onCancel={() => {
              post.reset();
              setComposing(false);
            }}
          />
        ) : (
          <Button
            onClick={() => {
              setClosing(null);
              setMarking(null);
              setComposing(true);
            }}
          >
            Send an update
          </Button>
        )
      ) : null}

      {err ? <Finding>{err}</Finding> : null}

      {updates.data && updates.data.updates.length > 0 ? (
        <ol className="m-0 list-none space-y-2 p-0" aria-label="Updates sent">
          {updates.data.updates.map((u) => (
            <li key={u.id} className="text-[13px]">
              <div className="flex items-center gap-2 text-[12px] text-[var(--ink-2)]">
                <span className="tabular">{clock(u.at)}</span>
                <span className="font-medium text-[var(--ink)]">
                  {UPDATE_WORDS[u.kind] ?? u.kind}
                  {u.subject_name ? ` · ${u.subject_name}` : ''}
                </span>
                {u.by_name ? <span>by {u.by_name}</span> : null}
                <span className="ml-auto">
                  {u.delivered ? (
                    <Status tone="safe">
                      {u.recipients !== null ? `Sent to ${u.recipients}` : 'Sent'}
                    </Status>
                  ) : (
                    <Status tone="attention">Not sent yet</Status>
                  )}
                </span>
              </div>
              <p className="mt-0.5">{u.body}</p>
            </li>
          ))}
        </ol>
      ) : updates.data ? (
        <p className="text-[13px] text-[var(--ink-2)]">
          Nothing has changed since the alert went out.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Closing a point: pick where people go instead, then the words everybody
 * receives — prefilled, shown before sending, editable. The redirect list
 * holds only OTHER open points, so a redirect to the point being closed or
 * to a closed one cannot be composed here, let alone sent.
 */
function ClosePointForm({
  point,
  openPoints,
  pending,
  onSend,
  onCancel,
}: {
  point: api.GroundPoint;
  openPoints: api.GroundPoint[];
  pending: boolean;
  onSend: (redirectToId: string, body: string) => void;
  onCancel: () => void;
}) {
  const [redirect, setRedirect] = useState(openPoints[0]?.id ?? '');
  const target = openPoints.find((o) => o.id === redirect);
  const [body, setBody] = useState(
    target ? `${point.name} is closed. Go to ${target.name}.` : `${point.name} is closed.`,
  );
  const [edited, setEdited] = useState(false);
  return (
    <form
      className="mt-2 space-y-2 rounded-[var(--radius-2)] border border-[var(--line)] p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (redirect && body.trim()) onSend(redirect, body.trim());
      }}
    >
      <label className="block text-[13px]">
        <span className="mb-1 block text-[var(--ink-2)]">Send people to</span>
        <select
          value={redirect}
          onChange={(e) => {
            const id = e.target.value;
            setRedirect(id);
            const t = openPoints.find((o) => o.id === id);
            if (!edited && t) setBody(`${point.name} is closed. Go to ${t.name}.`);
          }}
          className="h-9 w-full rounded-[var(--radius-1)] border border-[var(--line-2)] bg-[var(--paper)] px-3 text-[14px] text-[var(--ink)]"
        >
          {openPoints.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-[13px]">
        <span className="mb-1 block text-[var(--ink-2)]">What everybody is told</span>
        <textarea
          value={body}
          rows={2}
          maxLength={1000}
          onChange={(e) => {
            setEdited(true);
            setBody(e.target.value);
          }}
          className="w-full rounded-[var(--radius-1)] border border-[var(--line-2)] bg-[var(--paper)] px-3 py-2 text-[14px] text-[var(--ink)]"
        />
      </label>
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={pending || !redirect || !body.trim()}>
          {pending ? 'Sending…' : `Close ${point.name} and tell everyone`}
        </Button>
        <Button variant="quiet" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** The words for a hazard or an update: shown before sending, never sent blank. */
function WordsForm({
  label,
  initial,
  send,
  pending,
  onSend,
  onCancel,
}: {
  label: string;
  initial: string;
  send: string;
  pending: boolean;
  onSend: (body: string) => void;
  onCancel: () => void;
}) {
  const [body, setBody] = useState(initial);
  return (
    <form
      className="mt-2 space-y-2 rounded-[var(--radius-2)] border border-[var(--line)] p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (body.trim()) onSend(body.trim());
      }}
    >
      <label className="block text-[13px]">
        <span className="mb-1 block text-[var(--ink-2)]">{label}</span>
        <textarea
          value={body}
          rows={2}
          maxLength={1000}
          autoFocus
          onChange={(e) => setBody(e.target.value)}
          className="w-full rounded-[var(--radius-1)] border border-[var(--line-2)] bg-[var(--paper)] px-3 py-2 text-[14px] text-[var(--ink)]"
        />
      </label>
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={pending || !body.trim()}>
          {pending ? 'Sending…' : send}
        </Button>
        <Button variant="quiet" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * What people on the ground report in. New readings first, those several
 * people agree on above the rest; the operator's decision is recorded once
 * and never flipped. Acting means sending an update in your own words from
 * the Ground pane — this only records that the reading was taken seriously.
 */
export function ReportsPane({
  orgId,
  eventId,
  direct,
}: {
  orgId: string;
  eventId: string;
  direct: ReturnType<typeof useDirect>;
}) {
  const qc = useQueryClient();
  const { reports } = direct;
  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'acted' | 'dismiss' }) =>
      api.decideReport(orgId, eventId, id, decision),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['reports', orgId, eventId] }),
  });
  const list = reports.data?.reports ?? [];
  const ordered = [...list].sort((a, b) => {
    if (a.state !== b.state) return a.state === 'new' ? -1 : b.state === 'new' ? 1 : 0;
    if (a.corroborations !== b.corroborations) return b.corroborations - a.corroborations;
    return a.at < b.at ? 1 : -1;
  });
  const err = decide.error ? api.errorMessage(decide.error) : null;

  if (reports.isError && !reports.data) {
    return (
      <Finding>
        Reports did not load.{' '}
        <Button variant="quiet" label="Retry reports" onClick={() => void reports.refetch()}>
          Retry
        </Button>
      </Finding>
    );
  }
  if (reports.data && list.length === 0) {
    return <p className="text-[13px] text-[var(--ink-2)]">Nobody has reported anything in.</p>;
  }
  return (
    <div className="space-y-3">
      {err ? <Finding>{err}</Finding> : null}
      <ul
        className="m-0 list-none divide-y divide-[var(--line)] p-0"
        aria-label="Reports from the ground"
      >
        {ordered.map((r) => {
          const where = reportPlace(r);
          const tone: Tone =
            r.state !== 'new' ? 'neutral' : r.kind === 'needs_help' ? 'alert' : 'attention';
          return (
            <li key={r.id} className={`py-2 text-[14px] ${r.state !== 'new' ? 'opacity-60' : ''}`}>
              <div className="flex items-center gap-2">
                <Status tone={tone}>{REPORT_WORDS[r.kind]}</Status>
                {where ? <span className="truncate font-medium">{where}</span> : null}
                {r.corroborations > 0 ? (
                  <span className="text-[12px] text-[var(--ink-2)]">
                    +{r.corroborations} {r.corroborations === 1 ? 'other' : 'others'} say the same
                  </span>
                ) : null}
                <span className="ml-auto text-[12px] text-[var(--ink-2)] tabular">
                  {clock(r.at)}
                </span>
              </div>
              {r.body ? <p className="mt-0.5 text-[13px]">“{r.body}”</p> : null}
              <div className="mt-1 flex items-center gap-2 text-[12px] text-[var(--ink-2)]">
                <span>{r.source === 'app' ? 'From the app' : r.source}</span>
                {r.has_photo ? <span>· with a photo</span> : null}
                {r.state === 'new' ? (
                  <span className="ml-auto flex gap-1.5">
                    <Button
                      disabled={decide.isPending}
                      label={`Mark this ${REPORT_WORDS[r.kind].toLowerCase()} report as acted on`}
                      onClick={() => decide.mutate({ id: r.id, decision: 'acted' })}
                    >
                      Acted on it
                    </Button>
                    <Button
                      variant="quiet"
                      disabled={decide.isPending}
                      label={`Dismiss this ${REPORT_WORDS[r.kind].toLowerCase()} report`}
                      onClick={() => decide.mutate({ id: r.id, decision: 'dismiss' })}
                    >
                      Dismiss
                    </Button>
                  </span>
                ) : (
                  <span className="ml-auto">{r.state === 'acted' ? 'Acted on' : 'Dismissed'}</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The points this procedure can send people to. A point with no `kind` comes
 * from an older API and is kept — hiding it would claim a building has no
 * points when the server simply did not say.
 */
export function groundPointsOf(
  points: api.GroundPoint[] | undefined,
  kind: api.AssemblyKind | null,
): api.GroundPoint[] {
  return (points ?? []).filter((p) => p.kind === undefined || p.kind === kind);
}

/** How many reports still wait on a decision — the pane's headline count. */
export function newReportCount(reports: api.InboundReport[] | undefined): number {
  return reports?.filter((r) => r.state === 'new').length ?? 0;
}

export function paneCount(n: number, word: string): string | undefined {
  return n === 0 ? undefined : `${n} ${word}`;
}

export type LiveLens = 'command' | 'warden' | 'security';

/** The order the right column's panes take under each lens. */
export const LENS_ORDER: Record<LiveLens, string[]> = {
  command: ['next', 'areas', 'ground', 'reports', 'command'],
  warden: ['areas', 'reports', 'ground', 'next', 'command'],
  security: ['command', 'ground', 'reports', 'next', 'areas'],
};

export const LENS_WORDS: Record<LiveLens, string> = {
  command: 'Command',
  warden: 'Warden',
  security: 'Security',
};

/** Reorder the panes for a lens; a pane the lens does not name keeps its place at the end. */
export function orderPanes<T extends { key: string; node: ReactNode }>(
  panes: T[],
  lens: LiveLens,
): T[] {
  const order = LENS_ORDER[lens];
  const rank = (k: string) => {
    const i = order.indexOf(k);
    return i === -1 ? order.length : i;
  };
  return [...panes].sort((a, b) => rank(a.key) - rank(b.key));
}
